import { unreachable } from 'utils/unreachable';

interface FieldShape {
  fieldName: string;
  fieldType?: string;
  fieldDataType?: string;
  optionsAvailable?: boolean;
}

export enum SourceType {
  Task = 'task',
  Project = 'project',
  ProjectPhase = 'project-phase',
  Timesheet = 'timesheet',
  User = 'user',
  Phase = 'phase',
  Sprint = 'sprint',
  Company = 'company',
  Role = 'role',
  StarMeta = 'starmeta',
  ActualStarMeta = 'actualstarmeta',
  EstimatedStarMeta = 'estimatedstarmeta',
  Invoice = 'invoice',
  CreditNote = 'creditnote',
  ResourceRequest = 'resourcerequest',
  ResourceAllocation = 'resource-allocation',
  RequestApproval = 'requestapproval',
  Financial = 'financial',
  Formula = 'formula',
  Epic = 'epic',
  Backlog = 'backlog',
  Sheet = 'sheet',
  BillingEvent = 'billing-event',
  Expense = 'expense',
  Policy = 'policy',
  SheetColumn = 'sheet_column',
  ExpenseReport = 'expense_report',
  RevenueEntry = 'revenue-entry',
  ProjectFinancialBudget = 'project-financial-budget',
  ProjectFinancialsBudget = 'project-financials-budget',
  Signal = 'signal_occurrence',
  LeaveMeta = 'leavemeta',
}

export enum FieldType {
  Boolean = 'Boolean',
  Number = 'Number',
  Text = 'Text',
  MultiLineText = 'MultiLineText',
  Date = 'Date',
  SingleSelect = 'SingleSelect',
  MultiSelect = 'MultiSelect',
  SingleUser = 'SingleUser',
  MultiUser = 'MultiUser',
  Assignee = 'Assignee',
  Estimate = 'Estimate',
  Note = 'Note',
}

export enum NativeFieldName {
  PhaseName = 'phaseName',
}

export const FIELD_ID_ASSIGNEE = -127;

export const UserTypeFields = [
  FieldType.SingleUser,
  FieldType.MultiUser,
  FieldType.Assignee,
];

const calculatedFieldTypeMap = {
  currentPhases: FieldType.Text,
  actualStartDate: FieldType.Date,
  actualCompletedDate: FieldType.Date,
  customerCompanyName: FieldType.Text,
  completionPercentage: FieldType.Number,
  projectTrackedHours: FieldType.Number,
  totalBillableHours: FieldType.Number,
  totalNonBillableHours: FieldType.Number,
  overallRating: FieldType.Number,
  lastMilestoneRating: FieldType.Number,
  projectAge: FieldType.Number,
  plannedDuration: FieldType.Number,
  completedTasks: FieldType.Number,
  overdueTasks: FieldType.Number,
  blockedTasks: FieldType.Number,
  atRiskTasks: FieldType.Number,
  approvalPending: FieldType.Number,
};

export const filterableFieldType = (field: FieldShape): FieldType => {
  if (field.fieldName === 'assignee') {
    return FieldType.Assignee;
  }

  if (field.fieldType === 'ASSIGNEE') {
    return FieldType.Assignee;
  }

  if (field?.fieldDataType === 'USER') {
    return field?.fieldType === 'SINGLE_SELECT'
      ? FieldType.SingleUser
      : FieldType.MultiUser;
  }

  if (field.fieldName === 'estimatedDueDate') {
    return FieldType.Estimate;
  }
  if (calculatedFieldTypeMap[field.fieldName]) {
    return calculatedFieldTypeMap[field.fieldName];
  }

  if (field.fieldName === 'projectOwner') {
    return FieldType.SingleUser;
  }

  if (field.fieldName === NativeFieldName.PhaseName) {
    return FieldType.Text;
  }

  if (field.fieldName === 'projectFinancialsBudgets') {
    return FieldType.MultiSelect;
  }

  const type = field.optionsAvailable ? field.fieldType : field.fieldDataType;

  switch (type) {
    case 'BOOLEAN':
      return FieldType.Boolean;
    case 'NUMBER':
      return FieldType.Number;
    case 'TEXT':
      return FieldType.Text;
    case 'MULTI_LINE_TEXT':
      return FieldType.MultiLineText;
    case 'DATE':
      return FieldType.Date;
    case 'USER':
      return field.fieldType === 'SINGLE_SELECT'
        ? FieldType.SingleUser
        : FieldType.MultiUser;
    case 'SINGLE_SELECT':
      return FieldType.SingleSelect;
    case 'MULTI_SELECT':
      return field.fieldDataType === 'NOTE'
        ? FieldType.Note
        : FieldType.MultiSelect;
    case 'NOTE':
      return FieldType.Note;
    default:
      return unreachable(type as never);
  }
};
